import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { EndorsementsList } from './TreasuryCheckEndorse';
import { RedemptionOrder } from './TreasuryCheckStubs';
import './treasury-irs.css';

const usd = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const NETS = { mainnet: 'Ethereum Mainnet', base: 'Base', arbitrum: 'Arbitrum', sepolia: 'Sepolia' };

// Verify link with the receiving-bank data (routing, account, check no, amount, payload fingerprint) built in
export const checkQrUrl = (c) => {
  const q = new URLSearchParams({ t: c.verify_token || '', ...(c.bank_qr ? { v: 'BETHCHK1', ...c.bank_qr } : {}) });
  return `${window.location.origin}/verify-treasury-check/${c.check_id}?${q}`;
};

export const TreasuryCheckFace = ({ c, verifyUrl, signer, sigUrl }) => {
  const r = c.reference || {};
  const net = NETS[c.network] || c.network || '';
  const no = String(c.check_no ?? 0).padStart(6, '0');
  const micro = `BETHCOIN EXCHANGE MARKET TREASURY RESERVE ${c.issuer_seal || ''} `.repeat(6);
  const tok = c.token_symbol === 'BETH' ? 'BETH USD' : 'BTCK';
  return (
    <section className="check-panel irs-face" data-testid="tc-print-face">
      <img src="/liberty-engraving.png" alt="" className="irs-liberty" data-testid="tc-print-liberty" />
      <div className="irs-holo-strip" data-testid="tc-print-holo-strip" aria-hidden="true"><span>{`BETHCOIN · SECURE · ${tok} · TENDER · BETHCOIN · SECURE · ${tok} · TENDER`}</span></div>
      <div className="relative flex justify-between items-start pl-[1.25in]">
        <div className="text-center flex-1 min-w-0 pr-3">
          <p className="irs-title text-[17px] leading-tight whitespace-nowrap">BETHCOIN EXCHANGE MARKET TREASURY</p>
          <p className="check-serif text-[10px] tracking-[0.3em] text-[#1f5e4a]">BETH DIGITAL TREASURY CHECK</p>
          <p className="irs-micro mt-0.5" data-testid="tc-print-microprint">{micro}</p>
          <p className="text-[8px] text-[#2c4a3f] mt-0.5">ERC-20 {tok === 'BETH USD' ? 'BETH USD stablecoin' : 'BTCK'}{net ? ` · ${net}` : ''}{r.cusip ? ` · Reference: U.S. Treasury ${r.security_type || ''} CUSIP ${r.cusip} · reopening ${r.auction_date}` : ''}</p>
          <p className="check-mono text-[7px] text-[#1f5e4a] mt-0.5" data-testid="tc-print-issuer-seal">ISSUER SEAL {c.issuer_seal?.slice(0, 34)}…</p>
        </div>
        <div className="relative text-right w-[1.75in]">
          <p className="check-mono text-[8px] text-[#2c4a3f]">{tok === 'BETH USD' ? 'BETH' : 'BTCK'}-{r.cusip || 'SERIES'}</p>
          <p className="check-mono font-semibold text-[13px]" data-testid="tc-print-check-no">No. {no}</p>
          <p className="text-[10px] mt-1"><span className="text-[#2c4a3f] text-[8px] mr-1">DATE</span><span className="border-b border-[#13362b] px-2 check-mono">{c.date}</span></p>
          <p className="irs-void text-[7px] font-semibold mt-1">VOID AFTER ONE YEAR</p>
          <span className="irs-tender" data-testid="tc-print-tender">TENDER</span>
        </div>
      </div>
      <div className="relative flex items-end gap-3 mt-4 pl-[1.25in]">
        <p className="check-serif text-[11px] font-bold tracking-widest">PAY</p>
        <p className="flex-1 border-b border-[#13362b] text-[12px] pb-0.5 check-serif" data-testid="tc-print-amount-words">{c.amount_words} <span className="tracking-[0.3em] text-[#7b8f86]">*******</span></p>
        <div className="irs-amount px-3 py-1 min-w-[1.55in] text-right" data-testid="tc-print-amount">
          <p className="check-mono font-bold text-[16px]">$**{usd(c.amount)}</p>
        </div>
      </div>
      <div className="relative flex items-start justify-between mt-3">
        <div className="w-[3.7in]">
          <p className="text-[8px] leading-tight text-[#2c4a3f]">PAY TO THE ORDER OF</p>
          <div className="mt-1 pl-3 border-l-2 border-[#1f5e4a]/40">
            <p className="text-[13px] font-semibold uppercase tracking-wide" data-testid="tc-print-payee">{c.payee?.name || c.payee_address}</p>
            <p className="check-mono text-[8px] text-[#2c4a3f] break-all">{c.payee_address}</p>
            <p className="text-[8px] text-[#2c4a3f]">{c.settle_line || `${usd(c.amount)} ${c.token_symbol === 'BETH' ? 'BETH USD (1 BETH = $1.00)' : 'BTCK at $1.00 face each'}`} · Drawer: {c.payload?.parties?.seller?.name || 'Treasury Reserve Wallet'}</p>
          </div>
          <p className="text-[8px] text-[#2c4a3f] mt-1.5">MEMO <span className="border-b border-[#13362b] text-[10px] text-[#13362b] px-1">{c.payload?.memo || c.payload?.services_contract_award?.contract_no || '\u2014'}</span></p>
        </div>
        <div className="flex items-end gap-3 pt-3">
          {/* Small Statue of Liberty next to signature */}
          <img src="/liberty-engraving.png" alt="" className="w-[0.6in] h-auto opacity-60" data-testid="tc-print-sig-liberty" />
          <div className="w-[2in] text-center">
            {sigUrl ? <img src={sigUrl} alt="Signature" className="h-[30px] mx-auto object-contain -mb-1" data-testid="tc-print-sig-image" /> : <p className="check-signature" data-testid="tc-print-signer">{signer || 'BETHCOIN Treasury'}</p>}
            <p className="border-t border-[#13362b] text-[7px] text-[#2c4a3f] pt-0.5 uppercase tracking-wider">Disbursing Officer · on-chain issue</p>
            <p className="text-[5px] text-[#2c4a3f] uppercase tracking-[0.2em] leading-none mt-px" data-testid="tc-print-without-recourse">Without Recourse</p>
          </div>
          <QRCodeSVG value={verifyUrl || checkQrUrl(c)} size={112} level="Q" marginSize={4} bgColor="#ffffff" fgColor="#000000" data-testid="tc-print-qr" />
        </div>
      </div>
      {/* Contract/Transaction Hash Bar */}
      <div className="relative mt-2 pt-1 border-t border-[#1f5e4a]/30">
        <div className="flex justify-between items-center text-[6px] text-[#2c4a3f]">
          <div className="flex gap-4">
            <span data-testid="tc-print-contract-hash">CONTRACT: <span className="check-mono">{c.contract || 'PENDING'}</span></span>
            <span data-testid="tc-print-tx-hash">TX: <span className="check-mono">{c.tx_hash ? `${c.tx_hash.slice(0, 18)}...${c.tx_hash.slice(-8)}` : 'PENDING'}</span></span>
            <span data-testid="tc-print-payload-hash">PAYLOAD: <span className="check-mono">{c.payload_hash ? `${c.payload_hash.slice(0, 18)}...` : 'PENDING'}</span></span>
          </div>
          <span className="check-mono">{net.toUpperCase()}</span>
        </div>
      </div>
      <p className="relative text-[6.5px] text-[#2c4a3f] mt-1 pr-[0.2in]" data-testid="tc-print-disclaimer">Issued by BETHCOIN Exchange Market Treasury Reserve. Redeemable dollar for dollar at face by the issuer. Treasury reopening CUSIP data on the face of this instrument represents the acquisition of treasury securities which is redeemable dollar for dollar upon receipt of this instrument.</p>
      <p className="relative text-[5px] leading-tight text-[#2c4a3f] uppercase tracking-wide pr-[0.2in]" data-testid="tc-print-not-government">This is not a U.S. Government or Department of the Treasury check. It may be presented for processing at any governmental or financial institution; acceptance is at the discretion of the receiving institution. Void where prohibited by law. All rights reserved.</p>
      <div className="check-micr absolute left-0 right-0 bottom-[0.14in] text-center" data-testid="tc-print-micr">{c.micr ? `⑈${no}⑈ ⑆${c.micr.routing}⑆ ${c.micr.account}⑈` : `⑈${no}⑈ ⑆${r.cusip || 'BETH00001'}⑆ ${c.contract?.slice(2, 14) || ''}⑈`}</div>
      {c.drawer_tin && (
        <div className="tc-tin-watermark absolute left-[0.3in] right-[0.3in] bottom-[0.13in] text-center pointer-events-none select-none" aria-hidden="true" data-testid="tc-print-tin-watermark">
          {`TIN ***-**-${String(c.drawer_tin).replace(/\D/g, '').slice(-4)} · `.repeat(8)}
        </div>
      )}
      {c.network === 'sepolia' && <span className="tc-test-stamp" data-testid="tc-print-test-stamp">TEST CHECK · SEPOLIA · NOT NEGOTIABLE</span>}
    </section>
  );
};

const F = ({ label, children, mono, testid }) => (
  <div className="mb-2">
    <p className="text-[8px] uppercase tracking-wider text-gray-500">{label}</p>
    <p className={`text-[10px] text-gray-900 [overflow-wrap:anywhere] ${mono ? 'check-mono text-[8px]' : ''}`} data-testid={testid}>{children || '\u2014'}</p>
  </div>
);
const who = (p) => (p ? [...new Set([p.name, p.email, p.wallet].filter(Boolean))].join(' · ') : '');

export const TreasuryCheckVoucher = ({ c }) => {
  const p = c.payload || {};
  const r = p.treasury_reference || {};
  const sc = p.services_contract_award || {};
  const parties = p.parties || {};
  return (
    <section className="check-panel check-stub" style={{ height: 'auto', minHeight: '3.75in' }} data-testid="tc-print-voucher">
      <div className="flex justify-between items-baseline border-b-2 border-gray-900 pb-1 mb-3 irs-stub-head">
        <p className="check-serif font-bold text-[12px]">VOUCHER STUB &mdash; PAYLOAD TRANSMITTAL ({p.version})</p>
        <p className="check-mono text-[10px]">No. {c.check_no ?? '-'} · {c.date}</p>
      </div>
      <div className="grid grid-cols-4 gap-4">
        <div>
          <F label="Treasury reference CUSIP" testid="tc-voucher-cusip">{r.cusip} · {r.security_term}</F>
          <F label="Reopening auction / maturity">{r.auction_date} / {r.maturity_date}</F>
          <F label="Coupon / high yield / price">{r.coupon_pct}% / {r.high_yield_pct}% / {r.price_per_100}</F>
          <F label="Reference data hash" mono testid="tc-voucher-ref-hash">{r.reference_hash}</F>
        </div>
        <div>
          <F label="Services contract no." testid="tc-voucher-contract-no">{sc.contract_no}</F>
          <F label="Description">{sc.description}</F>
          <F label="Award amount / date">{sc.award_amount_usd != null ? `$${usd(sc.award_amount_usd)}` : ''}{sc.award_date ? ` · ${sc.award_date}` : ''}</F>
          <F label="Funding">{p.funding}</F>
        </div>
        <div>
          <F label="Buyer" testid="tc-voucher-buyer">{who(parties.buyer)}</F>
          <F label="Seller" testid="tc-voucher-seller">{who(parties.seller)}</F>
          <F label="Transferee">{who(parties.transferee)}</F>
          <F label="Assignee">{who(parties.assignee)}</F>
        </div>
        <div>
          <F label="Payload hash (on-chain)" mono testid="tc-voucher-payload-hash">{c.payload_hash}</F>
          <F label="Issue transaction" mono testid="tc-voucher-tx">{c.tx_hash}</F>
          <F label="Check contract" mono>{c.contract}</F>
          <F label="Collateral snapshot hash" mono>{p.collateral_hash}</F>
          <F label="Issuer seal hash" mono testid="tc-voucher-issuer-seal">{c.issuer_seal}</F>
        </div>
      </div>
      <RedemptionOrder c={c} />
      {c.endorsements?.length > 0 && <EndorsementsList items={c.endorsements} light />}
    </section>
  );
};
